import { cachedRequest, invalidateRequestCache } from '../utils/requestCache';
import { apiClient } from './apiClient';

const CACHE_PREFIX = 'commercial-projects:';

function invalidateProjectsCache(): void {
  invalidateRequestCache(CACHE_PREFIX);
}

export type ProjectClientSummary = {
  companyId: string;
  companyName: string;
  document?: string | null;
  contractCount: number;
};

export type ProjectClientListResult = {
  items: ProjectClientSummary[];
  totalCount: number;
  page: number;
  pageSize: number;
};

export type Project = {
  id: string;
  contractId: string;
  contractTitle: string;
  contractCode: string;
  companyId: string;
  companyName: string;
  code: string;
  name: string;
  description?: string | null;
  status: string;
  scopeStatement?: string | null;
  workProducts?: string | null;
  sizeEstimate?: number | null;
  sizeUnit?: string | null;
  lifecycleModel: string;
  lifecyclePhases?: string | null;
  effortEstimateHours?: number | null;
  costEstimate?: number | null;
  budgetAmount?: number | null;
  scheduleNotes?: string | null;
  plannedStartDateUtc?: string | null;
  plannedEndDateUtc?: string | null;
  baselineStartDateUtc?: string | null;
  baselineEndDateUtc?: string | null;
  actualStartDateUtc?: string | null;
  actualEndDateUtc?: string | null;
  resourcesPlan?: string | null;
  infrastructureNotes?: string | null;
  feasibilityAssessment?: string | null;
  stakeholderCommitmentObtained: boolean;
  stakeholderCommitmentAtUtc?: string | null;
  monitoringNotes?: string | null;
  actualEffortHours?: number | null;
  actualCost?: number | null;
  ownerUserId?: string | null;
  ownerUserName?: string | null;
  notes?: string | null;
  createdAtUtc: string;
  updatedAtUtc: string;
};

export type ProjectListResult = {
  items: Project[];
  totalCount: number;
  page: number;
  pageSize: number;
};

export type ProjectUpsertPayload = {
  contractId: string;
  code?: string;
  name: string;
  description?: string;
  status?: string;
  scopeStatement?: string;
  workProducts?: string;
  sizeEstimate?: number | null;
  sizeUnit?: string;
  lifecycleModel?: string;
  lifecyclePhases?: string;
  effortEstimateHours?: number | null;
  costEstimate?: number | null;
  budgetAmount?: number | null;
  scheduleNotes?: string;
  plannedStartDateUtc?: string | null;
  plannedEndDateUtc?: string | null;
  baselineStartDateUtc?: string | null;
  baselineEndDateUtc?: string | null;
  actualStartDateUtc?: string | null;
  actualEndDateUtc?: string | null;
  resourcesPlan?: string;
  infrastructureNotes?: string;
  feasibilityAssessment?: string;
  stakeholderCommitmentObtained?: boolean;
  stakeholderCommitmentAtUtc?: string | null;
  monitoringNotes?: string;
  actualEffortHours?: number | null;
  actualCost?: number | null;
  ownerUserId?: string | null;
  notes?: string;
};

export async function listProjectClients(params: {
  lifecycle?: string;
  search?: string;
  page?: number;
  pageSize?: number;
}): Promise<ProjectClientListResult> {
  const cacheKey = `${CACHE_PREFIX}clients:${params.lifecycle ?? ''}:${params.search ?? ''}:${params.page ?? 1}:${params.pageSize ?? 50}`;
  return cachedRequest(cacheKey, async () => {
    const response = await apiClient.get<ProjectClientListResult>('/v1/commercial/projects/clients', {
      params,
    });
    return response.data;
  });
}

export async function listProjects(params: {
  contractId?: string;
  companyId?: string;
  status?: string;
  search?: string;
  page?: number;
  pageSize?: number;
}): Promise<ProjectListResult> {
  const cacheKey = `${CACHE_PREFIX}list:${params.contractId ?? ''}:${params.companyId ?? ''}:${params.status ?? ''}:${params.search ?? ''}:${params.page ?? 1}:${params.pageSize ?? 50}`;
  return cachedRequest(cacheKey, async () => {
    const response = await apiClient.get<ProjectListResult>('/v1/commercial/projects', { params });
    return response.data;
  });
}

export async function getProject(id: string): Promise<Project> {
  return cachedRequest(`${CACHE_PREFIX}detail:${id}`, async () => {
    const response = await apiClient.get<Project>(`/v1/commercial/projects/${id}`);
    return response.data;
  });
}

export async function createProject(payload: ProjectUpsertPayload): Promise<Project> {
  const response = await apiClient.post<Project>('/v1/commercial/projects', payload);
  invalidateProjectsCache();
  return response.data;
}

export async function updateProject(id: string, payload: ProjectUpsertPayload): Promise<Project> {
  const response = await apiClient.put<Project>(`/v1/commercial/projects/${id}`, payload);
  invalidateProjectsCache();
  return response.data;
}

export async function deleteProject(id: string): Promise<void> {
  await apiClient.delete(`/v1/commercial/projects/${id}`);
  invalidateProjectsCache();
}

export async function duplicateProject(id: string): Promise<Project> {
  const response = await apiClient.post<Project>(`/v1/commercial/projects/${id}/duplicate`);
  invalidateProjectsCache();
  return response.data;
}

// ── Workspace types ───────────────────────────────────────────────────────────

export type ProjectStakeholder = {
  id: string;
  projectId: string;
  contactId?: string | null;
  appUserId?: string | null;
  appUserName?: string | null;
  name: string;
  role?: string | null;
  organization?: string | null;
  email?: string | null;
  phone?: string | null;
  influence: string;
  interest: string;
  engagementPlan?: string | null;
  commitmentStatus: string;
  notes?: string | null;
  createdAtUtc: string;
  updatedAtUtc: string;
};

export type ProjectStakeholderUpsertPayload = {
  contactId?: string | null;
  appUserId?: string | null;
  name: string;
  role?: string;
  organization?: string;
  email?: string;
  phone?: string;
  influence?: string;
  interest?: string;
  engagementPlan?: string;
  commitmentStatus?: string;
  notes?: string;
};

export type ProjectBacklogApprover = {
  id: string;
  contactId: string;
  contactName: string;
  obligation: string;
  decision: string;
  decidedAtUtc?: string | null;
};

export type ProjectBacklogApproverUpsert = {
  contactId: string;
  obligation: string;
  decision?: string;
  contactName?: string;
};

export type ProjectBacklogItem = {
  id: string;
  projectId: string;
  title: string;
  description?: string | null;
  priority: string;
  status: string;
  estimatePoints?: number | null;
  estimateHours?: number | null;
  sortOrder: number;
  assigneeUserId?: string | null;
  assigneeUserName?: string | null;
  tags?: string | null;
  clientVisible: boolean;
  approvers?: ProjectBacklogApprover[];
  createdAtUtc: string;
  updatedAtUtc: string;
  externalLinks?: ProjectBacklogExternalLink[];
};

export type ProjectBacklogExternalLink = {
  id: string;
  projectBacklogItemId: string;
  platform: string;
  externalTitle?: string | null;
  displayTitle: string;
  externalUrl?: string | null;
  externalId?: string | null;
  externalType?: string | null;
  notes?: string | null;
  status: 'Pending' | 'Linked' | string;
  linkedAtUtc?: string | null;
  createdAtUtc: string;
  updatedAtUtc: string;
};

export type ProjectBacklogExternalLinkUpsertPayload = {
  platform: string;
  externalTitle?: string;
  externalUrl?: string;
  externalId?: string;
  externalType?: string;
  notes?: string;
};

export type ProjectBacklogItemUpsertPayload = {
  title: string;
  description?: string;
  priority?: string;
  status?: string;
  estimatePoints?: number | null;
  estimateHours?: number | null;
  sortOrder?: number;
  assigneeUserId?: string | null;
  tags?: string;
  clientVisible?: boolean;
  approvers?: ProjectBacklogApproverUpsert[];
};

export type ProjectRoadmapItem = {
  id: string;
  projectId: string;
  title: string;
  description?: string | null;
  kind: string;
  status: string;
  plannedDateUtc?: string | null;
  actualDateUtc?: string | null;
  sortOrder: number;
  createdAtUtc: string;
  updatedAtUtc: string;
};

export type ProjectRoadmapItemUpsertPayload = {
  title: string;
  description?: string;
  kind?: string;
  status?: string;
  plannedDateUtc?: string | null;
  actualDateUtc?: string | null;
  sortOrder?: number;
};

export type ProjectRisk = {
  id: string;
  projectId: string;
  title: string;
  description?: string | null;
  probability: string;
  impact: string;
  treatmentPriority: number;
  mitigationPlan?: string | null;
  contingencyPlan?: string | null;
  status: string;
  ownerUserId?: string | null;
  ownerUserName?: string | null;
  identifiedAtUtc: string;
  closedAtUtc?: string | null;
  createdAtUtc: string;
  updatedAtUtc: string;
};

export type ProjectRiskUpsertPayload = {
  title: string;
  description?: string;
  probability?: string;
  impact?: string;
  treatmentPriority?: number;
  mitigationPlan?: string;
  contingencyPlan?: string;
  status?: string;
  ownerUserId?: string | null;
  identifiedAtUtc?: string;
  closedAtUtc?: string | null;
};

export type ProjectIssue = {
  id: string;
  projectId: string;
  title: string;
  description?: string | null;
  severity: string;
  status: string;
  correctiveAction?: string | null;
  assignedToUserId?: string | null;
  assignedToUserName?: string | null;
  identifiedAtUtc: string;
  resolvedAtUtc?: string | null;
  createdAtUtc: string;
  updatedAtUtc: string;
};

export type ProjectIssueUpsertPayload = {
  title: string;
  description?: string;
  severity?: string;
  status?: string;
  correctiveAction?: string;
  assignedToUserId?: string | null;
  identifiedAtUtc?: string;
  resolvedAtUtc?: string | null;
};

export type ProjectChangeRequest = {
  id: string;
  projectId: string;
  title: string;
  description?: string | null;
  impactScope?: string | null;
  impactSchedule?: string | null;
  impactCost?: string | null;
  status: string;
  requestedByUserId: string;
  requestedByUserName?: string | null;
  reviewedByUserId?: string | null;
  reviewedByUserName?: string | null;
  submittedAtUtc?: string | null;
  reviewedAtUtc?: string | null;
  reviewNotes?: string | null;
  rejectionReason?: string | null;
  createdAtUtc: string;
  updatedAtUtc: string;
};

export type ProjectChangeRequestUpsertPayload = {
  title: string;
  description?: string;
  impactScope?: string;
  impactSchedule?: string;
  impactCost?: string;
};

export type ProjectChangeRequestRejectPayload = {
  rejectionReason: string;
};

export type ProjectMeeting = {
  id: string;
  projectId: string;
  meetingType: string;
  otherTypeNote?: string | null;
  title: string;
  scheduledAtUtc: string;
  durationMinutes?: number | null;
  locationOrLink?: string | null;
  attendees?: string | null;
  minutes?: string | null;
  decisions?: string | null;
  actionItems?: string | null;
  notes?: string | null;
  createdAtUtc: string;
  updatedAtUtc: string;
  participants?: ProjectMeetingParticipant[];
};

export type ProjectMeetingParticipant = {
  id: string;
  meetingId: string;
  stakeholderId: string;
  name: string;
  role?: string | null;
  organization?: string | null;
};

export type ProjectMeetingUpsertPayload = {
  meetingType?: string;
  otherTypeNote?: string;
  title: string;
  scheduledAtUtc: string;
  durationMinutes?: number | null;
  locationOrLink?: string;
  attendees?: string;
  minutes?: string;
  decisions?: string;
  actionItems?: string;
  notes?: string;
  stakeholderIds?: string[];
};

export type ProjectStoredFile = {
  id: string;
  projectId: string;
  projectMeetingId?: string;
  title: string;
  kind?: string | null;
  source: string;
  fileName?: string | null;
  externalUrl?: string | null;
  createdAtUtc: string;
};

export type ProjectStoredFileDownload = {
  url: string;
  fileName: string;
};

function projectPath(projectId: string, suffix: string): string {
  return `/v1/commercial/projects/${projectId}${suffix}`;
}

// ── Stakeholders ────────────────────────────────────────────────────────────

export async function listProjectStakeholders(projectId: string): Promise<ProjectStakeholder[]> {
  return cachedRequest(`${CACHE_PREFIX}stakeholders:${projectId}`, async () => {
    const response = await apiClient.get<ProjectStakeholder[]>(projectPath(projectId, '/stakeholders'));
    return response.data;
  });
}

export async function createProjectStakeholder(
  projectId: string,
  payload: ProjectStakeholderUpsertPayload,
): Promise<ProjectStakeholder> {
  const response = await apiClient.post<ProjectStakeholder>(
    projectPath(projectId, '/stakeholders'),
    payload,
  );
  invalidateProjectsCache();
  return response.data;
}

export async function updateProjectStakeholder(
  projectId: string,
  id: string,
  payload: ProjectStakeholderUpsertPayload,
): Promise<ProjectStakeholder> {
  const response = await apiClient.put<ProjectStakeholder>(
    projectPath(projectId, `/stakeholders/${id}`),
    payload,
  );
  invalidateProjectsCache();
  return response.data;
}

export async function deleteProjectStakeholder(projectId: string, id: string): Promise<void> {
  await apiClient.delete(projectPath(projectId, `/stakeholders/${id}`));
  invalidateProjectsCache();
}

// ── Backlog ─────────────────────────────────────────────────────────────────

export async function listProjectBacklogItems(projectId: string): Promise<ProjectBacklogItem[]> {
  return cachedRequest(`${CACHE_PREFIX}backlog:${projectId}`, async () => {
    const response = await apiClient.get<ProjectBacklogItem[]>(projectPath(projectId, '/backlog'));
    return response.data;
  });
}

export async function createProjectBacklogItem(
  projectId: string,
  payload: ProjectBacklogItemUpsertPayload,
): Promise<ProjectBacklogItem> {
  const response = await apiClient.post<ProjectBacklogItem>(projectPath(projectId, '/backlog'), payload);
  invalidateProjectsCache();
  return response.data;
}

export async function assignProjectBacklogItem(
  projectId: string,
  id: string,
  payload: { assigneeUserId?: string | null },
): Promise<ProjectBacklogItem> {
  const response = await apiClient.post<ProjectBacklogItem>(
    projectPath(projectId, `/backlog/${id}/assign`),
    payload,
  );
  invalidateProjectsCache();
  return response.data;
}

export async function updateProjectBacklogItem(
  projectId: string,
  id: string,
  payload: ProjectBacklogItemUpsertPayload,
): Promise<ProjectBacklogItem> {
  const response = await apiClient.put<ProjectBacklogItem>(
    projectPath(projectId, `/backlog/${id}`),
    payload,
  );
  invalidateProjectsCache();
  return response.data;
}

export async function deleteProjectBacklogItem(projectId: string, id: string): Promise<void> {
  await apiClient.delete(projectPath(projectId, `/backlog/${id}`));
  invalidateProjectsCache();
}

export type ProjectRequirement = {
  id: string;
  projectId: string;
  title: string;
  validationCriteria: string;
  backlogItemCount: number;
  createdAtUtc: string;
  updatedAtUtc: string;
};

export type ProjectRequirementUpsertPayload = {
  title: string;
  validationCriteria: string;
};

export async function listProjectRequirements(projectId: string): Promise<ProjectRequirement[]> {
  return cachedRequest(`${CACHE_PREFIX}requirements:${projectId}`, async () => {
    const response = await apiClient.get<ProjectRequirement[]>(projectPath(projectId, '/requirements'));
    return response.data;
  });
}

export async function createProjectRequirement(
  projectId: string,
  payload: ProjectRequirementUpsertPayload,
): Promise<ProjectRequirement> {
  const response = await apiClient.post<ProjectRequirement>(projectPath(projectId, '/requirements'), payload);
  invalidateProjectsCache();
  return response.data;
}

export async function updateProjectRequirement(
  projectId: string,
  id: string,
  payload: ProjectRequirementUpsertPayload,
): Promise<ProjectRequirement> {
  const response = await apiClient.put<ProjectRequirement>(
    projectPath(projectId, `/requirements/${id}`),
    payload,
  );
  invalidateProjectsCache();
  return response.data;
}

export async function deleteProjectRequirement(projectId: string, id: string): Promise<void> {
  await apiClient.delete(projectPath(projectId, `/requirements/${id}`));
  invalidateProjectsCache();
}

export async function listProjectBacklogRequirements(
  projectId: string,
  backlogItemId: string,
): Promise<ProjectRequirement[]> {
  const response = await apiClient.get<ProjectRequirement[]>(
    projectPath(projectId, `/backlog/${backlogItemId}/requirements`),
  );
  return response.data;
}

export async function linkProjectBacklogRequirement(
  projectId: string,
  backlogItemId: string,
  requirementId: string,
): Promise<ProjectRequirement> {
  const response = await apiClient.post<ProjectRequirement>(
    projectPath(projectId, `/backlog/${backlogItemId}/requirements/${requirementId}`),
  );
  invalidateProjectsCache();
  return response.data;
}

export async function unlinkProjectBacklogRequirement(
  projectId: string,
  backlogItemId: string,
  requirementId: string,
): Promise<void> {
  await apiClient.delete(projectPath(projectId, `/backlog/${backlogItemId}/requirements/${requirementId}`));
  invalidateProjectsCache();
}

// ── Backlog external links ────────────────────────────────────────────────────

export async function listProjectBacklogExternalLinks(
  projectId: string,
  backlogItemId: string,
): Promise<ProjectBacklogExternalLink[]> {
  const response = await apiClient.get<ProjectBacklogExternalLink[]>(
    projectPath(projectId, `/backlog/${backlogItemId}/external-links`),
  );
  return response.data;
}

export async function createProjectBacklogExternalLink(
  projectId: string,
  backlogItemId: string,
  payload: ProjectBacklogExternalLinkUpsertPayload,
): Promise<ProjectBacklogExternalLink> {
  const response = await apiClient.post<ProjectBacklogExternalLink>(
    projectPath(projectId, `/backlog/${backlogItemId}/external-links`),
    payload,
  );
  invalidateProjectsCache();
  return response.data;
}

export async function updateProjectBacklogExternalLink(
  projectId: string,
  backlogItemId: string,
  linkId: string,
  payload: ProjectBacklogExternalLinkUpsertPayload,
): Promise<ProjectBacklogExternalLink> {
  const response = await apiClient.put<ProjectBacklogExternalLink>(
    projectPath(projectId, `/backlog/${backlogItemId}/external-links/${linkId}`),
    payload,
  );
  invalidateProjectsCache();
  return response.data;
}

export async function deleteProjectBacklogExternalLink(
  projectId: string,
  backlogItemId: string,
  linkId: string,
): Promise<void> {
  await apiClient.delete(projectPath(projectId, `/backlog/${backlogItemId}/external-links/${linkId}`));
  invalidateProjectsCache();
}

// ── Roadmap ───────────────────────────────────────────────────────────────────

export async function listProjectRoadmapItems(projectId: string): Promise<ProjectRoadmapItem[]> {
  return cachedRequest(`${CACHE_PREFIX}roadmap:${projectId}`, async () => {
    const response = await apiClient.get<ProjectRoadmapItem[]>(projectPath(projectId, '/roadmap'));
    return response.data;
  });
}

export async function createProjectRoadmapItem(
  projectId: string,
  payload: ProjectRoadmapItemUpsertPayload,
): Promise<ProjectRoadmapItem> {
  const response = await apiClient.post<ProjectRoadmapItem>(projectPath(projectId, '/roadmap'), payload);
  invalidateProjectsCache();
  return response.data;
}

export async function updateProjectRoadmapItem(
  projectId: string,
  id: string,
  payload: ProjectRoadmapItemUpsertPayload,
): Promise<ProjectRoadmapItem> {
  const response = await apiClient.put<ProjectRoadmapItem>(
    projectPath(projectId, `/roadmap/${id}`),
    payload,
  );
  invalidateProjectsCache();
  return response.data;
}

export async function deleteProjectRoadmapItem(projectId: string, id: string): Promise<void> {
  await apiClient.delete(projectPath(projectId, `/roadmap/${id}`));
  invalidateProjectsCache();
}

// ── Risks ───────────────────────────────────────────────────────────────────

export async function listProjectRisks(projectId: string): Promise<ProjectRisk[]> {
  return cachedRequest(`${CACHE_PREFIX}risks:${projectId}`, async () => {
    const response = await apiClient.get<ProjectRisk[]>(projectPath(projectId, '/risks'));
    return response.data;
  });
}

export async function createProjectRisk(
  projectId: string,
  payload: ProjectRiskUpsertPayload,
): Promise<ProjectRisk> {
  const response = await apiClient.post<ProjectRisk>(projectPath(projectId, '/risks'), payload);
  invalidateProjectsCache();
  return response.data;
}

export async function updateProjectRisk(
  projectId: string,
  id: string,
  payload: ProjectRiskUpsertPayload,
): Promise<ProjectRisk> {
  const response = await apiClient.put<ProjectRisk>(projectPath(projectId, `/risks/${id}`), payload);
  invalidateProjectsCache();
  return response.data;
}

export async function deleteProjectRisk(projectId: string, id: string): Promise<void> {
  await apiClient.delete(projectPath(projectId, `/risks/${id}`));
  invalidateProjectsCache();
}

// ── Issues ────────────────────────────────────────────────────────────────────

export async function listProjectIssues(projectId: string): Promise<ProjectIssue[]> {
  return cachedRequest(`${CACHE_PREFIX}issues:${projectId}`, async () => {
    const response = await apiClient.get<ProjectIssue[]>(projectPath(projectId, '/issues'));
    return response.data;
  });
}

export async function createProjectIssue(
  projectId: string,
  payload: ProjectIssueUpsertPayload,
): Promise<ProjectIssue> {
  const response = await apiClient.post<ProjectIssue>(projectPath(projectId, '/issues'), payload);
  invalidateProjectsCache();
  return response.data;
}

export async function updateProjectIssue(
  projectId: string,
  id: string,
  payload: ProjectIssueUpsertPayload,
): Promise<ProjectIssue> {
  const response = await apiClient.put<ProjectIssue>(projectPath(projectId, `/issues/${id}`), payload);
  invalidateProjectsCache();
  return response.data;
}

export async function deleteProjectIssue(projectId: string, id: string): Promise<void> {
  await apiClient.delete(projectPath(projectId, `/issues/${id}`));
  invalidateProjectsCache();
}

// ── Change requests ───────────────────────────────────────────────────────────

export async function listProjectChangeRequests(projectId: string): Promise<ProjectChangeRequest[]> {
  return cachedRequest(`${CACHE_PREFIX}changes:${projectId}`, async () => {
    const response = await apiClient.get<ProjectChangeRequest[]>(projectPath(projectId, '/changes'));
    return response.data;
  });
}

export async function createProjectChangeRequest(
  projectId: string,
  payload: ProjectChangeRequestUpsertPayload,
): Promise<ProjectChangeRequest> {
  const response = await apiClient.post<ProjectChangeRequest>(projectPath(projectId, '/changes'), payload);
  invalidateProjectsCache();
  return response.data;
}

export async function updateProjectChangeRequest(
  projectId: string,
  id: string,
  payload: ProjectChangeRequestUpsertPayload,
): Promise<ProjectChangeRequest> {
  const response = await apiClient.put<ProjectChangeRequest>(
    projectPath(projectId, `/changes/${id}`),
    payload,
  );
  invalidateProjectsCache();
  return response.data;
}

export async function submitProjectChangeRequest(
  projectId: string,
  id: string,
): Promise<ProjectChangeRequest> {
  const response = await apiClient.post<ProjectChangeRequest>(
    projectPath(projectId, `/changes/${id}/submit`),
  );
  invalidateProjectsCache();
  return response.data;
}

export async function approveProjectChangeRequest(
  projectId: string,
  id: string,
): Promise<ProjectChangeRequest> {
  const response = await apiClient.post<ProjectChangeRequest>(
    projectPath(projectId, `/changes/${id}/approve`),
  );
  invalidateProjectsCache();
  return response.data;
}

export async function rejectProjectChangeRequest(
  projectId: string,
  id: string,
  payload: ProjectChangeRequestRejectPayload,
): Promise<ProjectChangeRequest> {
  const response = await apiClient.post<ProjectChangeRequest>(
    projectPath(projectId, `/changes/${id}/reject`),
    payload,
  );
  invalidateProjectsCache();
  return response.data;
}

export async function deleteProjectChangeRequest(projectId: string, id: string): Promise<void> {
  await apiClient.delete(projectPath(projectId, `/changes/${id}`));
  invalidateProjectsCache();
}

// ── Meetings ──────────────────────────────────────────────────────────────────

export async function listProjectMeetings(projectId: string): Promise<ProjectMeeting[]> {
  return cachedRequest(`${CACHE_PREFIX}meetings:${projectId}`, async () => {
    const response = await apiClient.get<ProjectMeeting[]>(projectPath(projectId, '/meetings'));
    return response.data;
  });
}

export async function createProjectMeeting(
  projectId: string,
  payload: ProjectMeetingUpsertPayload,
): Promise<ProjectMeeting> {
  const response = await apiClient.post<ProjectMeeting>(projectPath(projectId, '/meetings'), payload);
  invalidateProjectsCache();
  return response.data;
}

export async function updateProjectMeeting(
  projectId: string,
  id: string,
  payload: ProjectMeetingUpsertPayload,
): Promise<ProjectMeeting> {
  const response = await apiClient.put<ProjectMeeting>(
    projectPath(projectId, `/meetings/${id}`),
    payload,
  );
  invalidateProjectsCache();
  return response.data;
}

export async function deleteProjectMeeting(projectId: string, id: string): Promise<void> {
  await apiClient.delete(projectPath(projectId, `/meetings/${id}`));
  invalidateProjectsCache();
}

export async function listProjectFiles(projectId: string): Promise<ProjectStoredFile[]> {
  const response = await apiClient.get<ProjectStoredFile[]>(projectPath(projectId, '/files'));
  return response.data ?? [];
}

export async function uploadProjectFile(projectId: string, file: File, title: string): Promise<ProjectStoredFile> {
  const body = new FormData();
  body.append('file', file);
  if (title.trim()) body.append('title', title.trim());
  const response = await apiClient.post<ProjectStoredFile>(projectPath(projectId, '/files'), body);
  return response.data;
}

export async function linkProjectFile(projectId: string, title: string, externalUrl: string): Promise<ProjectStoredFile> {
  const response = await apiClient.post<ProjectStoredFile>(projectPath(projectId, '/files/link'), {
    title,
    externalUrl,
  });
  return response.data;
}

export async function getProjectFileDownloadUrl(projectId: string, fileId: string): Promise<ProjectStoredFileDownload> {
  const response = await apiClient.get<ProjectStoredFileDownload>(
    projectPath(projectId, `/files/${fileId}/download-url`),
  );
  return response.data;
}

export async function deleteProjectFile(projectId: string, fileId: string): Promise<void> {
  await apiClient.delete(projectPath(projectId, `/files/${fileId}`));
}

export async function listProjectMeetingFiles(projectId: string, meetingId: string): Promise<ProjectStoredFile[]> {
  const response = await apiClient.get<ProjectStoredFile[]>(
    projectPath(projectId, `/meetings/${meetingId}/files`),
  );
  return response.data ?? [];
}

export async function uploadProjectMeetingFile(
  projectId: string,
  meetingId: string,
  file: File,
  title: string,
  kind: string,
): Promise<ProjectStoredFile> {
  const body = new FormData();
  body.append('file', file);
  if (title.trim()) body.append('title', title.trim());
  body.append('kind', kind);
  const response = await apiClient.post<ProjectStoredFile>(
    projectPath(projectId, `/meetings/${meetingId}/files`),
    body,
  );
  return response.data;
}

export async function linkProjectMeetingFile(
  projectId: string,
  meetingId: string,
  title: string,
  externalUrl: string,
  kind: string,
): Promise<ProjectStoredFile> {
  const response = await apiClient.post<ProjectStoredFile>(
    projectPath(projectId, `/meetings/${meetingId}/files/link`),
    { title, externalUrl, kind },
  );
  return response.data;
}

export async function getProjectMeetingFileDownloadUrl(
  projectId: string,
  meetingId: string,
  fileId: string,
): Promise<ProjectStoredFileDownload> {
  const response = await apiClient.get<ProjectStoredFileDownload>(
    projectPath(projectId, `/meetings/${meetingId}/files/${fileId}/download-url`),
  );
  return response.data;
}

export async function deleteProjectMeetingFile(projectId: string, meetingId: string, fileId: string): Promise<void> {
  await apiClient.delete(projectPath(projectId, `/meetings/${meetingId}/files/${fileId}`));
}
